import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { XIcon } from 'lucide-react';
import { AccountMode, useAccount } from '../contexts/AccountContext';
type Status = 'idle' | 'loading' | 'success';
interface Fields {
  name: string;
  email: string;
  password: string;
}
const emptyFields: Fields = {
  name: '',
  email: '',
  password: ''
};
const tabs: {
  id: AccountMode;
  label: string;
}[] = [{
  id: 'signin',
  label: 'Sign in'
}, {
  id: 'signup',
  label: 'Create account'
}];
export function AccountDialog() {
  const {
    isOpen,
    mode,
    setMode,
    closeAccount
  } = useAccount();
  const [fields, setFields] = useState<Fields>(emptyFields);
  const [errors, setErrors] = useState<Partial<Fields>>({});
  const [status, setStatus] = useState<Status>('idle');
  const isSignup = mode === 'signup';
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeAccount();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [closeAccount]);
  useEffect(() => {
    if (!isOpen) {
      const timeout = window.setTimeout(() => {
        setFields(emptyFields);
        setErrors({});
        setStatus('idle');
      }, 400);
      return () => window.clearTimeout(timeout);
    }
  }, [isOpen]);
  const switchMode = (next: AccountMode) => {
    setMode(next);
    setErrors({});
    setStatus('idle');
  };
  const update = (key: keyof Fields, value: string) => {
    setFields(f => ({
      ...f,
      [key]: value
    }));
    setErrors(e => ({
      ...e,
      [key]: undefined
    }));
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const next: Partial<Fields> = {};
    if (isSignup && fields.name.trim().length < 2) next.name = 'Please enter your name.';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(fields.email)) next.email = 'Please enter a valid email address.';
    if (fields.password.length < 8) next.password = 'Use at least eight characters.';
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    setStatus('loading');
    window.setTimeout(() => setStatus('success'), 900);
  };
  return <AnimatePresence data-id="element-12" data-mp="3j9cpxphgdr7e">
      {isOpen && <div className="fixed inset-0 z-[60] flex items-center justify-center px-4 py-10" data-id="element-13" data-mp="3o1dvyto12loz">
          <motion.div key="scrim" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.35
      }} onClick={closeAccount} className="absolute inset-0 bg-ink/30 backdrop-blur-[2px]" aria-hidden="true" data-id="element-14" data-mp="3s3968nnxs9ew" />

          <motion.div key="dialog" role="dialog" aria-modal="true" aria-labelledby="account-title" initial={{
        opacity: 0,
        y: 16,
        scale: 0.985
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 12,
        scale: 0.99
      }} transition={{
        duration: 0.45,
        ease: [0.22, 1, 0.36, 1]
      }} className="relative w-full max-w-[26rem] overflow-y-auto bg-bone px-8 py-10 shadow-[0_24px_80px_-30px_rgba(38,35,32,0.45)] sm:px-10" style={{
        maxHeight: '90vh'
      }} data-id="element-15" data-mp="34cd8zb0pom09">
            <button type="button" onClick={closeAccount} aria-label="Close" className="absolute right-5 top-5 text-smoke transition-colors hover:text-ink" data-id="element-16" data-mp="ozungzn8ju3p">
              <XIcon size={18} strokeWidth={1.25} data-id="element-17" data-mp="g1ixmzu710cb" />
            </button>

            <p className="eyebrow text-clay" data-id="element-18" data-mp="b03a914jmhqr">Tenzy Club</p>
            <h2 id="account-title" className="mt-3 font-display text-3xl font-light leading-tight text-ink" data-id="element-19" data-mp="621vuti2b6lm">
              {isSignup ? 'Create an account' : 'Welcome back'}
            </h2>
            <p className="mt-2 text-sm font-light leading-relaxed text-smoke" data-id="element-20" data-mp="20gcgeprmjell">
              {isSignup ? 'Save your sizes and cuts, and follow each small run.' : 'Sign in for your orders and saved sizes.'}
            </p>

            <div role="tablist" aria-label="Account mode" className="mt-8 flex gap-7 border-b border-greige/70" data-id="element-21" data-mp="23fuur5td1hdg">
              {tabs.map(tab => <button key={tab.id} role="tab" type="button" aria-selected={mode === tab.id} onClick={() => switchMode(tab.id)} className={`eyebrow relative pb-3 transition-colors ${mode === tab.id ? 'text-ink' : 'text-smoke hover:text-ink'}`} data-id="element-22" data-mp="1yikwd0nztvdh">
                  {tab.label}
                  <span className={`absolute inset-x-0 bottom-0 h-px origin-left bg-ink transition-transform duration-500 ${mode === tab.id ? 'scale-x-100' : 'scale-x-0'}`} data-id="element-23" data-mp="1ft4ry296tirs" />
                </button>)}
            </div>

            {status === 'success' ? <div className="mt-9" role="status" data-id="element-24" data-mp="i6iezxbwqyer">
                <p className="font-display text-2xl font-light text-ink" data-id="element-25" data-mp="i7tswpz2hmjl">
                  {isSignup ? 'Your account is ready.' : 'You are signed in.'}
                </p>
                <p className="mt-3 text-sm font-light leading-relaxed text-smoke" data-id="element-26" data-mp="s46jhezbfka4">
                  {isSignup ? `We have sent a note to ${fields.email}. Welcome to the house.` : 'Your saved sizes and orders are waiting for you.'}
                </p>
                <button type="button" onClick={closeAccount} className="eyebrow mt-8 w-full border border-ink bg-ink py-4 text-bone transition-colors hover:bg-transparent hover:text-ink" data-id="element-27" data-mp="1fusikgrcdu7z">
                  Continue shopping
                </button>
              </div> : <form onSubmit={handleSubmit} noValidate className="mt-8 space-y-5" data-id="element-28" data-mp="1jxp3ie4cu2f3">
                <AnimatePresence initial={false} data-id="element-29" data-mp="hk1226n3huyj">
                  {isSignup && <motion.div key="name" initial={{
              opacity: 0,
              height: 0
            }} animate={{
              opacity: 1,
              height: 'auto'
            }} exit={{
              opacity: 0,
              height: 0
            }} transition={{
              duration: 0.35,
              ease: [0.22, 1, 0.36, 1]
            }} className="overflow-hidden" data-id="element-30" data-mp="miccaihay7ha">
                      <Field id="account-name" label="Full name" type="text" autoComplete="name" value={fields.name} error={errors.name} onChange={v => update('name', v)} data-id="element-31" data-mp="156zgyzvkguuc" />
                    </motion.div>}
                </AnimatePresence>

                <Field id="account-email" label="Email" type="email" autoComplete="email" value={fields.email} error={errors.email} onChange={v => update('email', v)} data-id="element-32" data-mp="3g1wtxwvnn7mg" />

                <Field id="account-password" label="Password" type="password" autoComplete={isSignup ? 'new-password' : 'current-password'} value={fields.password} error={errors.password} hint={isSignup ? 'At least eight characters.' : undefined} onChange={v => update('password', v)} data-id="element-33" data-mp="2upfu4fat0jha" />

                {!isSignup && <div className="flex justify-end" data-id="element-34" data-mp="3di05ujydg7xv">
                    <button type="button" className="text-[11px] uppercase tracking-[0.16em] text-smoke underline underline-offset-4 transition-colors hover:text-ink" data-id="element-35" data-mp="270d3ihy6zgan">
                      Forgotten password
                    </button>
                  </div>}

                <button type="submit" disabled={status === 'loading'} className="eyebrow w-full border border-ink bg-ink py-4 text-bone transition-colors hover:bg-transparent hover:text-ink disabled:opacity-50" data-id="element-36" data-mp="9wmhaa9tkkea">
                  {status === 'loading' ? 'One moment…' : isSignup ? 'Create account' : 'Sign in'}
                </button>

                <p className="text-xs font-light leading-relaxed text-smoke" data-id="element-37" data-mp="ey1zlukm4csz">
                  {isSignup ? 'By creating an account you agree to our terms and privacy notice.' : <>
                      New here?{' '}
                      <button type="button" onClick={() => switchMode('signup')} className="underline underline-offset-4 transition-colors hover:text-ink" data-id="element-38" data-mp="17kwjap9lv85e">
                        Create an account
                      </button>
                    </>}
                </p>
              </form>}
          </motion.div>
        </div>}
    </AnimatePresence>;
}
interface FieldProps {
  id: string;
  label: string;
  type: string;
  value: string;
  autoComplete: string;
  error?: string;
  hint?: string;
  onChange: (value: string) => void;
}
function Field({
  id,
  label,
  type,
  value,
  autoComplete,
  error,
  hint,
  onChange
}: FieldProps) {
  return <div data-id="element-39" data-mp="2n78hl48zdybf">
      <label htmlFor={id} className="eyebrow text-smoke" data-id="element-40" data-mp="2n6ysdivrfv6s">
        {label}
      </label>
      <input id={id} type={type} value={value} autoComplete={autoComplete} aria-invalid={Boolean(error)} aria-describedby={error ? `${id}-error` : hint ? `${id}-hint` : undefined} onChange={e => onChange(e.target.value)} className="mt-2 w-full border-b border-greige bg-transparent py-2.5 text-sm font-light text-ink transition-colors focus:border-ink focus:outline-none" data-id="element-41" data-mp="2i512i5suu24v" />
      {error ? <p id={`${id}-error`} role="alert" className="mt-2 text-[11px] tracking-wide text-clay" data-id="element-42" data-mp="95lm8mrhzlb">
          {error}
        </p> : hint ? <p id={`${id}-hint`} className="mt-2 text-[11px] tracking-wide text-smoke" data-id="element-43" data-mp="3sdhnsv2w46cb">
          {hint}
        </p> : null}
    </div>;
}